<template>
  <div style="margin: 0 1rem 0 0">
    <SettingGroup title="カテゴリー・リスト">
      <div
        class="categoryListHeader"
        :style="{ backgroundColor: CSS_COLORS.GRAYSCALE5 }"
      >
        <div />
        <div style="display: flex; align-items: center; text-align: center">
          <div style="padding: 0 0.75rem">並替</div>
          <div>カテゴリー名</div>
        </div>
        <div>アイコン</div>
      </div>

      <div
        class="categoryListWrapper"
        :style="{
          border: `1px solid ${CSS_COLORS.GRAYSCALE2}`,
          height: '10rem',
          overflowY: 'auto',
          scrollbarGutter: 'stable'
        }"
      >
        <!-- Vue.Draggable (Vue2) / デフォルトスロット + v-for -->
        <draggable :list="menuCategoryList" tag="ul" class="categoryList" handle=".categoryHandle">
          <li
            v-for="(element, idx) in menuCategoryList"
            :key="element.id"
            class="categoryItem"
            :class="{ selected: selectedCategoryId === element.id }"
            @click="handleClickCategory(element.id)"
          >
            <div class="categoryHandle">☰</div>

            <el-form :model="element" :rules="rules" ref="categoryForm">
              <el-form-item prop="Category">
                <FormTextBox
                  ref="inputRef"
                  v-model="element.Category"
                  :disable="false"
                  prop="Category"
                />
              </el-form-item>
            </el-form>

            <!-- SelectBox: Vue2のslot構文 + nullガード -->
            <SelectBox
              :defaultValue="element.Icon || null"
              :options="CATEGORY_ICON_LIST"
              @update="handleChangeCategoryIcon(element.id, $event)"
            >
              <template slot="label" slot-scope="{ selected }">
                <IconImg :src="(selected && selected.label) || ''" />
              </template>
              <template slot="option" slot-scope="{ item }">
                <IconImg :src="(item && item.label) || ''" />
              </template>
            </SelectBox>

            <!-- アイコンだけの小さめボタンに調整 -->
            <BaseButton
                type="default"
                :transparent="true"
                :disable="element.NonDeletable?.Category"
                @click.stop="handleRemoveCategory(element)"
            >
              <img
                src="@/assets/images/delete.svg"
                alt="カテゴリー削除"
                :style="{
                  opacity: (!element.NonDeletable?.Category) ? 1 : 0.3,
                  cursor: (!element.NonDeletable?.Category) ? 'pointer' : 'not-allowed',
                  width: '1.2em',
                  height: '1.2em'
                }"
              />
            </BaseButton>
          </li>
        </draggable>
      </div>

      <div style="display: flex; justify-content: flex-end; margin: 0 2rem 0 0">
        <BaseButton
          class="icon-only-btn"
          type="default"
          size="default"
          label="新規作成"
          icon="Plus"
          :disable="!enableAddCategory"
          @click="handleAddCategory"
        >
        </BaseButton>
      </div>
    </SettingGroup>

    <SettingGroup title="機能リスト">
      <div class="transferWrapper">
        <Transfer
          v-if="isReady"
          :transferData="transferOptions"
          :initialSelected="initialSelected"
          :exclude-disabled-from-right="selectedCategoryId !== null"
          leftPanelTitle="機能リスト"
          rightPanelTitle="選択可能な機能"
          @select="handleChangeFunction"
          :draggable="true"
        />
      </div>
    </SettingGroup>
  </div>
</template>

<script>
import humps from 'humps'
import draggable from 'vuedraggable'

import Encoding from 'encoding-japanese'
import BaseButton from '@/components/pc/radio/ipradio/atoms/BaseButton.vue'
import FormTextBox from '@/components/pc/radio/ipradio/atoms/FormTextBox.vue'
import SelectBox from '@/components/pc/radio/ipradio/atoms/SelectBox.vue'
import Transfer from '@/components/pc/radio/ipradio/atoms/Transfer.vue'
import SettingGroup from '@/components/pc/radio/ipradio/organisms/layout/SettingGroup.vue'
import { CATEGORY_ICON_LIST, FUNCTION_LIST } from '@/components/pc/radio/ipradio/config/BCconstants'
import { CSS_COLORS } from '@/components/pc/radio/ipradio/config/css-constants'
import { useRadioSettingsStore } from '@/components/pc/radio/ipradio/stores/bcRadioSettings'

export default {
  name: 'RadioMenuSettingsVue2',
  components: {
    draggable,
    BaseButton,
    FormTextBox,
    SelectBox,
    Transfer,
    SettingGroup,
    IconImg: {
      props: { src: String },
      render(h) {
        return h('div', { style: { display: 'flex', alignItems: 'center' } }, [
          h('img', {
            attrs: { src: this.src || '', alt: 'icon' },
            style: { width: '1.5em', height: '1.5em' }
          })
        ])
      }
    }
  },
  props: {
    keyType: { type: String, default: 'menu' }
  },
  data() {
    const radioSettings = useRadioSettingsStore()
    return {
      radioSettings,
      setting: radioSettings.getTempSetting(),
      menuCategoryList: [],
      selectedCategoryId: null,
      isReady: false,
      CSS_COLORS,
      CATEGORY_ICON_LIST,
      rules: {},
    }
  },
  computed: {
    menuKey() {
      return humps.pascalize(this.keyType)
    },
    enableAddCategory() {
      return this.menuCategoryList.length < 12
    },
    backlightEnabled() {
      const targets = ['SideKey', 'Front', 'MicKey']
      return targets.some((key) => {
        const obj = this.setting.KeyAssignment[key]
        if (!obj) return false
        return Object.values(obj).some(
          (item) =>
            typeof item === 'object' &&
            item !== null &&
            Object.values(item).some((value) => value === 'Backlight')
        )
      })
    },
    transferOptions() {
      const sel = this.menuCategoryList.find((el) => el.id === this.selectedCategoryId)
      const selectCategoryFunctions = (sel && sel.Function) || []

      // 追加：このカテゴリで「選択不可」にする Function キー一覧
      const nonDeletableFunctions = Array.isArray(sel?.NonDeletable?.Function)
        ? sel.NonDeletable.Function
        : []

      // 追加：他のカテゴリで既に使われている Function キーは「選択可能」から除外する
      // ※ただし、現在選択中カテゴリで既に選択済みのキーは表示する
      const usedInOtherCategories = new Set(
        this.menuCategoryList
          .filter((el) => el.id !== this.selectedCategoryId)
          .flatMap((el) => {
            const funcs = Array.isArray(el?.Function) ? el.Function : []
            const nonDel = Array.isArray(el?.NonDeletable?.Function) ? el.NonDeletable.Function : []
            return [...funcs, ...nonDel]
          })
      )

      const orgList = FUNCTION_LIST
        .map((ele) => {
          if (!ele.type[this.keyType]) return undefined
          return { label: ele.label, key: ele.value }
        })
        .filter(Boolean)
        // 他カテゴリで使用済みの機能は除外（ただし現在カテゴリで選択済みのものは残す）
        .filter((ele) => !usedInOtherCategories.has(ele.key) || selectCategoryFunctions.includes(ele.key))
        // バックライトが有効な場合、AutoBacklightを完全に除外
        .filter((ele) => !(ele.key === 'AutoBacklight' && this.backlightEnabled))

      const sortedList = [
        ...(selectCategoryFunctions.map((key) => orgList.find((ele) => ele.key === key)) || []),
        ...orgList.filter((ele) => !selectCategoryFunctions.includes(ele.key))
      ]
        .filter(Boolean)
        .map((el) => {
          const isNonDeletableFunction = nonDeletableFunctions.includes(el.key)
          return {
            ...el,
            disabled: this.selectedCategoryId === null || isNonDeletableFunction
          }
        })

      return sortedList
    },
    initialSelected() {
      const sel = this.menuCategoryList.find(el => el.id === this.selectedCategoryId)
      return sel ? sel.Function : []
    },
    disableSave() {
      return this.menuCategoryList.some(category => !category.Category || category.Category.trim() === '')
    },
  },
  watch: {
    menuCategoryList: {
      deep: true,
      handler() {
        this.transCategoryList2Setting()
        this.radioSettings.saveTempSetting(this.setting)
      }
    },
    'menuCategoryList.length'() {
      this.menuCategoryList.forEach((el, idx) => {
        this.$set(el, 'id', idx)
      })
    },
    disableSave: {
      immediate: true,
      handler(val) {
        this.$emit('disableSave', val)
      }
    }
  },
  created() {
    this.transSetting2CategoryList()
    this.isReady = true
    this.rules = {
      Category: [
        {
          validator: (rule, value, callback) => {
            if (!value || value.trim() === '') {
              callback(new Error('値を指定してください。'))
              return
            }
            callback()
          },
          trigger: ['change', 'blur']
        },
        { validator: this.validateCategoryJIS, trigger: ['change', 'blur'] }
      ],
    }
  },
  methods: {
    transCategoryList2Setting() {
      const data = this.setting.KeyAssignment[this.menuKey]
      data.length = 0
      data.push(
        ...this.menuCategoryList.map((el) => {
          const nonDeletableFunctions = Array.isArray(el?.NonDeletable?.Function)
            ? el.NonDeletable.Function
            : []

          const functions = Array.isArray(el?.Function) ? el.Function : []

          // ★ ここだけで保証する（保存時に必ず復元）
          const mergedFunctions = Array.from(new Set([
            ...nonDeletableFunctions,
            ...functions,
          ]))

          return {
            Category: el.Category,
            Icon: el.Icon,
            Function: mergedFunctions,
            ...(el.NonDeletable != null ? { NonDeletable: el.NonDeletable } : {}),
          }
        })
      )
    },
    transSetting2CategoryList() {
      const data = this.setting.KeyAssignment[this.menuKey] || []
      this.menuCategoryList = data.map((el, idx) => ({ ...el, id: idx }))
    },
    handleAddCategory() {
      this.menuCategoryList.push({
        id: this.menuCategoryList.length,
        Category: `項目 ${this.menuCategoryList.length + 1}`,
        Icon: this.CATEGORY_ICON_LIST[0].value,
        Function: []
      })
      this.selectedCategoryId = this.menuCategoryList.length - 1
    },
    handleRemoveCategory(element) {
      const i = this.menuCategoryList.indexOf(element)
      if (i >= 0) this.menuCategoryList.splice(i, 1)
      this.selectedCategoryId = null
    },
    handleClickCategory(id) {
      this.selectedCategoryId = id
    },
    handleChangeCategoryIcon(id, value) {
      const category = this.menuCategoryList.find((el) => el.id === id)
      if (category) category.Icon = value
    },
    handleChangeFunction(val) {
      if (this.selectedCategoryId !== null) {
        const cat = this.menuCategoryList.find((el) => el.id === this.selectedCategoryId)
        if (cat) {
          cat.Function = Array.isArray(val) ? val : []
        }
      }
    },
    validateCategoryJIS(rule, value, callback) {
      if (!value) return callback();

      // 定義
      const allowedKu = (ku) => (ku >= 1 && ku <= 8) || ku === 13 || (ku >= 16 && ku <= 84);

      const encodeSjis = (s) => Encoding.convert(Encoding.stringToCode(s), { to: 'SJIS', from: 'UNICODE', type: 'array' });

      const decodeSjis = (bytes) => Encoding.codeToString(Encoding.convert(bytes, { to: 'UNICODE', from: 'SJIS', type: 'array' }));

      const sjisToKuTen = (b1, b2) => {
        let j1 =
          (b1 >= 0x81 && b1 <= 0x9F) ? (b1 - 0x81) * 2 + 0x21 :
          (b1 >= 0xE0 && b1 <= 0xEF) ? (b1 - 0xC1) * 2 + 0x21 :
          null;
        if (j1 == null) return null;

        let j2;
        if (b2 >= 0x9F && b2 <= 0xFC) { j1 += 1; j2 = b2 - 0x7E; }
        else if (b2 >= 0x40 && b2 <= 0x7E) j2 = b2 - 0x1F;
        else if (b2 >= 0x80 && b2 <= 0x9E) j2 = b2 - 0x20;
        else return null;

        const ku = j1 - 0x20, ten = j2 - 0x20;
        return (ku >= 1 && ku <= 94 && ten >= 1 && ten <= 94) ? { ku, ten } : null;
      };
      // 定義


      // フィルタしつつ SJIS 換算14バイト以内で打ち切り
      let out = '';
      let used = 0;

      for (const ch of value) {
        const cp = ch.codePointAt(0);
        if (cp < 0x20 && ch !== ' ') continue;

        let bytes;
        try { bytes = encodeSjis(ch); } catch { continue; }
        if (!bytes || bytes.length < 1 || bytes.length > 2) continue;

        // 往復一致（置換・互換変換を排除）
        if (decodeSjis(bytes) !== ch) continue;

        if (bytes.length === 1) {
          const b = bytes[0];
          if (!((b >= 0x20 && b <= 0x7E) || (b >= 0xA1 && b <= 0xDF))) continue;
        } else {
          const kt = sjisToKuTen(bytes[0], bytes[1]);
          if (!kt || !allowedKu(kt.ku)) continue;
        }

        if (used + bytes.length > 14) break; // 元コード同様「入らない最初の文字」で打ち切り
        out += ch;
        used += bytes.length;
      }

      const target = this.menuCategoryList.find((el) => el.Category === value);
      if (target) target.Category = out;

      callback();
    },
  }
}
</script>

<style scoped>
li:hover {
    background: #ecf1fa;
}

/* === layout === */
.categoryListHeader {
  display: grid;
  grid-template-columns: 2rem 1fr 1fr 2rem 1rem;
  gap: 8px;
  align-items: center;
  font-weight: bold;
}
.categoryList {
  padding: 0;
  margin: 0 2rem;
}
.categoryItem {
  display: grid;
  grid-template-columns: 2rem 1fr 1fr 2rem 1rem;
  gap: 8px;
  align-items: center;
  line-height: normal; /* ← 画像が沈むのを防ぐ */
  cursor: pointer;
  transition: background 0.2s;
}
/* 項目の上下間隔を作る */
.categoryItem + .categoryItem {
  margin-top: 16px; /* エラーメッセージ表示用余白 */
}
.categoryItem.selected {
    background: #ecf1fa;
}
.categoryHandle {
  display: flex;
  justify-content: center;
  cursor: grab;
}
.addCategoryButton { width: 200px; }

/* BaseButton 内 el-button をコンパクト化＆中央寄せ */
.icon-only-btn >>> .el-button {
  padding: 0;
  min-width: 0;
  width: 28px;      /* 小さめの正方形 */
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

/* アイコンは少し大きめに */
.icon-only-btn__img {
  width: 18px;
  height: 18px;
  display: block;
}

/* element-ui の調整は既存通り */
:deep(.el-form-item) { margin-bottom: 0; }
:deep(.el-transfer-panel) { width: calc(500px - 1rem); }
:deep(.el-transfer-panel__body),
:deep(.el-transfer-panel__list) { height: 470px; }
</style>
